import { useState, useEffect, useCallback } from 'react';
import { SCHOOL_THEMES, INCUBS_THEME, type SchoolTheme } from '../lib/schoolThemes';

const THEME_KEY       = 'incubs-school-theme';
const BGSTYLE_KEY     = 'incubs-bg-style';
const BGINTENSITY_KEY = 'incubs-bg-intensity';

export type BgStyle     = 'orbs' | 'gradient' | 'aurora' | 'mesh' | 'dots' | 'waves' | 'minimal';
export type BgIntensity = 'subtle' | 'normal' | 'vivid';

export const BG_INTENSITY_MULTIPLIER: Record<BgIntensity, number> = {
  subtle: 0.45,
  normal: 1.0,
  vivid:  1.7,
};

// ─── Module-level shared store ────────────────────────────────────────────────
// All hook instances read from and write to these, so a change in one component
// (e.g. Sidebar) immediately updates every other consumer (e.g. DiscoverPage).

let _theme:       SchoolTheme = INCUBS_THEME;
let _bgStyle:     BgStyle     = 'orbs';
let _bgIntensity: BgIntensity = 'normal';
let _initialized              = false;

const _themeListeners:     Set<(t: SchoolTheme) => void> = new Set();
const _bgListeners:        Set<(b: BgStyle)     => void> = new Set();
const _intensityListeners: Set<(i: BgIntensity) => void> = new Set();

function initFromStorage() {
  if (_initialized) return;
  _initialized = true;

  const savedTheme = localStorage.getItem(THEME_KEY);
  if (savedTheme) {
    const found = SCHOOL_THEMES.find(t => t.id === savedTheme);
    if (found) _theme = found;
  }

  const savedBg = localStorage.getItem(BGSTYLE_KEY) as BgStyle | null;
  if (savedBg) _bgStyle = savedBg;

  const savedIntensity = localStorage.getItem(BGINTENSITY_KEY) as BgIntensity | null;
  if (savedIntensity) _bgIntensity = savedIntensity;
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

export function useSchoolTheme() {
  initFromStorage();

  const [theme,       setTheme]       = useState<SchoolTheme>(_theme);
  const [bgStyle,     setBgStyle]     = useState<BgStyle>(_bgStyle);
  const [bgIntensity, setBgIntensity] = useState<BgIntensity>(_bgIntensity);

  // Subscribe to cross-instance updates
  useEffect(() => {
    _themeListeners.add(setTheme);
    _bgListeners.add(setBgStyle);
    _intensityListeners.add(setBgIntensity);
    return () => {
      _themeListeners.delete(setTheme);
      _bgListeners.delete(setBgStyle);
      _intensityListeners.delete(setBgIntensity);
    };
  }, []);

  const updateTheme = useCallback((id: string) => {
    const found = SCHOOL_THEMES.find(t => t.id === id);
    if (!found) return;
    _theme = found;
    localStorage.setItem(THEME_KEY, id);
    _themeListeners.forEach(fn => fn(found));
  }, []);

  const updateBgStyle = useCallback((style: BgStyle) => {
    _bgStyle = style;
    localStorage.setItem(BGSTYLE_KEY, style);
    _bgListeners.forEach(fn => fn(style));
  }, []);

  const updateBgIntensity = useCallback((intensity: BgIntensity) => {
    _bgIntensity = intensity;
    localStorage.setItem(BGINTENSITY_KEY, intensity);
    _intensityListeners.forEach(fn => fn(intensity));
  }, []);

  return { theme, updateTheme, allThemes: SCHOOL_THEMES, bgStyle, updateBgStyle, bgIntensity, updateBgIntensity };
}
